// PromptPreview.tsx

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'; 
import { fetchSystemPrompt } from '../services/api/virtualAssistant';
import { Download, Cpu } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import 'katex/dist/katex.min.css';

// Add polyfill for Object.hasOwn
if (!Object.hasOwn) {
  Object.hasOwn = function(obj, prop) {
    return Object.prototype.hasOwnProperty.call(obj, prop);
  };
}

interface PromptPreviewProps {
    isPPreviewModalOpen: boolean;
    setIsPromptPreviewModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    instructions: string;
    assistantId: any;
}

interface SystemPromptData {
  system_prompt: string;
  token_count: number;
}

const PromptPreview: React.FC<PromptPreviewProps> = ({
    isPPreviewModalOpen,
    setIsPromptPreviewModalOpen,
    instructions,
    assistantId
}) => {
  const [previewData, setPreviewData] = useState<string>("");
  const [tokenCount, setTokenCount] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  
  const handleOverlayClick = () => {
    setIsPromptPreviewModalOpen(false);
  };

  useEffect(() => {
    //  console.log("Preview Data:", previewData);
  }, [previewData]);

  const handleModalClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
  };

  // Function to handle direct JSON input
  const parseSystemPromptJson = (jsonData: string): boolean => {
    try {
      const data = JSON.parse(jsonData) as SystemPromptData;
      setPreviewData(data.system_prompt);
      setTokenCount(data.token_count ? data.token_count.toString() : "");
      return true;
    } catch (error) {
      console.error('Error parsing JSON data:', error);
      setPreviewData('**Failed to parse JSON data.**');
      return false;
    }
  };

  const getPromptPreview = async() => {
    try {
      setLoading(true);
      const details = await fetchSystemPrompt(assistantId);
      
      // Check if the response is already a string
      if (typeof details?.system_prompt === 'string') {
        setPreviewData(details?.system_prompt);
        setTokenCount(details?.token_count);
      } 
      // Check if the response might be a JSON string that needs parsing
      else if (typeof details === 'string' && details.trim().startsWith('{')) {
        parseSystemPromptJson(details);
      }
      // Otherwise, it might be an object
      else if (details && typeof details === 'object') {
        setPreviewData(details.system_prompt || '');
        setTokenCount(details.token_count ? details.token_count.toString() : '');
      }
    } catch (error) {
      console.error('Error fetching prompt preview:', error);
      setPreviewData('**Failed to load preview data.**');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if(isPPreviewModalOpen){
      getPromptPreview();
    }
  }, [isPPreviewModalOpen]);

  const handleDownload = () => {
    if (!previewData) return;

    const plainText = previewData
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '') // strip remaining HTML tags
  

    // Create a plain text version for download
    const blob = new Blob([plainText], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
  
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prompt-preview.txt';
    a.click();
  
    window.URL.revokeObjectURL(url);
  };

  
  // Define markdown components with proper TypeScript typing
  const markdownComponents: Components = {
  h1: ({children}) => <h1 style={{fontSize: '1.5em', fontWeight: 'bold', marginTop: '16px', marginBottom: '8px'}}>{children}</h1>,
  h2: ({children}) => <h2 style={{fontSize: '1.3em', fontWeight: 'bold', marginTop: '14px', marginBottom: '7px'}}>{children}</h2>,
  h3: ({children}) => <h3 style={{fontSize: '1.1em', fontWeight: 'bold', marginTop: '12px', marginBottom: '6px'}}>{children}</h3>,
  p: ({children}) => {
  return (
    <p style={{ marginBottom: "8px", whiteSpace: "pre-line" }}>
      {React.Children.map(children, (child, index) => {
        if (typeof child === 'string') {
          const parts = child.split(/(NOTE:)/g);
          return parts.map((part, i) =>
            part === "NOTE:" ? (
              <span key={`${index}-${i}`} style={{ color: "red", fontWeight: "bold" }}>
                {part}
              </span>
            ) : (
              part
            )
          );
        }
        return child; // preserve React elements like <em>
      })}
    </p>
  );
},

  strong: ({children}) => <strong style={{fontWeight: 'bold'}}>{children}</strong>,
  em: ({children}) => <em style={{fontStyle: 'italic'}}>{children}</em>,
  ul: ({children}) => <ul style={{marginLeft: '20px', listStyleType: 'disc', marginBottom: '10px'}}>{children}</ul>,
  ol: ({children}) => <ol style={{marginLeft: '20px', listStyleType: 'decimal', marginBottom: '10px'}}>{children}</ol>,
  li: ({children}) => <li style={{marginBottom: '4px'}}>{children}</li>,
  blockquote: ({children}) => <blockquote style={{borderLeft: '4px solid #e2e8f0', paddingLeft: '16px', marginLeft: '0', marginRight: '0', fontStyle: 'italic'}}>{children}</blockquote>,
  code: ({node, inline, className, children, ...props}: any) => {
    if (inline) {
      return <code style={{backgroundColor: '#EBE7E7', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace'}} {...props}>{children}</code>;
    }
    return (
      <pre style={{
          backgroundColor: '#EBE7E7',
          padding: '12px', 
          borderRadius: '5px',
          fontFamily: 'monospace',
          marginBottom: '10px',
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-word'  }}>
        <code {...props}>{children}</code>
      </pre>
    );
  }
};

// Process any LaTeX formulas in markdown for proper display

const cleanMarkdown = (content: string) => {
  try {
    const parsed = JSON.parse(`"${content.replace(/"/g, '\\"')}"`);
    return stripLeadingSpaces(parsed.trim());
  } catch {
    return stripLeadingSpaces(content.replace(/\\n/g, '\n'));
  }
};

const stripLeadingSpaces = (text :string) => {
  return text
    .split('\n')
    .map(line => line.replace(/^\s+/, '')) // remove leading spaces from each line
    .join('\n');
};

const processLatexFormulas = (content: string): string => {
  // First, protect any escaped dollar signs
  let processed = content.replace(/\\\$/g, '___ESCAPED_DOLLAR___');
  
  // Replace LaTeX expressions with backtick-wrapped expressions
  processed = processed.replace(/\$(.*?)\$/g, '`$$$1$$`');
  
  // Handle any remaining dollar signs (likely currency)
  processed = processed.replace(/\$/g, '\\$');
  
  // Restore escaped dollar signs
  processed = processed.replace(/___ESCAPED_DOLLAR___/g, '\\$');
  
  return processed
    .replace(/\\n/g, '\n'); // literal "\n" → real newline
};

// Function to safely process content with potential LaTeX
const safeProcessContent = (content: string): string => {
  // First protect code blocks
  const codeBlocks: string[] = [];
  const withoutCode = content.replace(/```[\s\S]*?```/g, (match) => {
    codeBlocks.push(match);
    return `___CODE_BLOCK_${codeBlocks.length - 1}___`;
  });

  // Then protect inline code
  const inlineCode: string[] = [];
  const withoutInlineCode = withoutCode.replace(/`[^`]+`/g, (match) => {
    inlineCode.push(match);
    return `___INLINE_CODE_${inlineCode.length - 1}___`;
  });

  // Process LaTeX in the remaining text
  const processedText = processLatexFormulas(withoutInlineCode);
  
  // Restore inline code
  let result = processedText;
  inlineCode.forEach((code, index) => {
    result = result.replace(`___INLINE_CODE_${index}___`, code);
  });
  
  // Restore code blocks
  codeBlocks.forEach((block, index) => {
    result = result.replace(`___CODE_BLOCK_${index}___`, block);
  });
  
  return result;
};


  return (
    <div 
      className={`fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-end z-50 ${isPPreviewModalOpen ? 'block' : 'hidden'}`}
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white w-[45%] h-[94%] shadow-lg transform transition-transform duration-300 ease-in-out" 
       
        onClick={handleModalClick}
      >
        <div className="p-6 flex flex-col h-full">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold mb-4">Prompt Preview</h2>
            {!loading && (
              <>
                <div className="flex items-center space-x-3 mt-2 bg-white border border-gray-200 shadow-sm rounded-xl px-4 py-2 w-fit">
                  <span className="text-sm font-semibold text-gray-700">Token:</span>
                  <span className="text-sm font-mono text-grey-600">{tokenCount}</span>
                </div>
              </>
            )}

            <div className="flex space-x-2 items-center mt-1">
              { previewData && 
                <button
                  className="flex items-center gap-1 px-3 py-1 bg-white border border-gray-500 rounded-full text-black font-semibold hover:bg-black hover:text-white ml-auto"
                  onClick={() => handleDownload()}
                >
                  <Download className="w-5 h-5" /> Download
                </button>
              }
              <button
                onClick={() => setIsPromptPreviewModalOpen(false)}
                className="text-black-500 hover:text-red-700"
              >
                <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-5">
            <p className="sub-heading pt-2">
              If your chatbot is pulling content from Canvas, Panopto or uploaded files, 
              the following instructions are dynamically injected in addition to the instructions that you have provided in the Prompt Template.
              These instructions consist of hardcoded and data-driven text and are not editable.
            </p>
            
            <hr className="mt-4"></hr>
            
            {loading ? (
              <div className="flex justify-center items-center h-20">
                <div className="dotted-circle">
                  {[...Array(10)].map((_, i) => (
                    <span key={i} className="dot" style={{ "--i": i } as React.CSSProperties}></span>
                  ))}
                </div>
              </div>
            ) : (
              previewData ? (
                <div className="text-sm pr-4 pt-1">
                  {/* Single consistent chat message container for all content */}
                  <div className="p-4 rounded-lg bg-gray-100 border border-gray-300 mt-4">
                    <div className="pl-3 chat-message-content">
                     <div className="prose max-w-none">
                      <ReactMarkdown
                        components={markdownComponents}
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[rehypeKatex]}
                      >
                        { safeProcessContent(cleanMarkdown(previewData)) }
                        </ReactMarkdown>
                    </div>
                    </div>
                  </div>
                </div>
              ) : ''
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptPreview;